import React, { useEffect, useRef, useState } from "react";
import styles from "../../../styles/about/components/AboutHero.module.css";
import heroImg from "../../../assets/images/image12.png";
import EventNotificationPopup from "../../../components/EventNotificationPopup";
import { FaCross, FaChevronDown } from "react-icons/fa";
import { MdOutlineMenuBook, MdGroups, MdFavoriteBorder } from "react-icons/md";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const CARDS = [
  {
    icon: <MdOutlineMenuBook />,
    label: "Our Mission",
    color: "#006BA6",
    heading: "Proclaim & Transform",
    points: [
      "Promote revival through repentance, fasting & prayer",
      "Preach the transformative Word of God",
      "Raise an army of intercessors for God's Kingdom",
      "Foster spiritual, social & economic transformation",
    ],
    verse: null,
  },
  {
    icon: <MdFavoriteBorder />,
    label: "Our Vision",
    color: "#ffd700",
    heading: "Life in Its Fullness",
    points: null,
    verse: {
      text: "\u201cImitate me, just as I also imitate Christ.\u201d",
      ref: "1 Corinthians 11:1",
      body:
        "Life in its fullness \u2014 God, People, and Resources \u2014 by imitating Jesus Christ.",
    },
  },
  {
    icon: <MdGroups />,
    label: "Who We Are",
    color: "#00c6ff",
    heading: "Spirit-Led Community",
    points: [
      "Worship \u2014 encountering God\u2019s presence",
      "Intercession \u2014 contending for His Kingdom",
      "Discipleship \u2014 growing in faith together",
      "Spreading the Gospel across every nation",
    ],
    verse: null,
    outcomes: ["Hope", "Healing", "Restoration"],
  },
];

export default function AboutHero() {
  const [cardsRef, cardsInView] = useInView(0.08);

  const scrollDown = () => {
    window.scrollBy({ top: window.innerHeight * 0.85, behavior: "smooth" });
  };

  return (
    <>
      <EventNotificationPopup />

      {/* ── Cinematic Hero ── */}
      <section className={styles.hero}>
        <div className={styles.bgWrap}>
          <img src={heroImg} alt="CWFM Ministry" className={styles.bgImage} />
        </div>
        <div className={styles.overlayMain} />
        <div className={styles.overlayVignette} />
        <div className={styles.lightRays} />

        <div className={styles.heroContent}>
          <div className={styles.eyebrowRow}>
            <span className={styles.eyebrowBadge}>
              <span className={styles.eyebrowDot} />
              About Us
            </span>
          </div>

          <h1 className={styles.heroTitle}>
            Calvary Wide{" "}
            <span className={styles.titleGold}>Fellowship</span>{" "}
            Ministries
          </h1>

          <div className={styles.titleBar} />

          <p className={styles.heroSub}>
            Learn about our calling, our passion for the Gospel, and the
            community God is shaping through CWFM. A ministry rooted in
            the Word and led by the Holy Spirit.
          </p>
        </div>

        {/* Scroll cue */}
        <button
          className={styles.scrollCue}
          onClick={scrollDown}
          aria-label="Scroll down"
        >
          <FaChevronDown />
        </button>
      </section>

      {/* ── Feature Cards ── */}
      <div
        ref={cardsRef}
        className={`${styles.cardsRow} ${cardsInView ? styles.cardsVisible : ""}`}
      >
        {CARDS.map((card, i) => (
          <div
            key={i}
            className={styles.card}
            style={{ transitionDelay: `${i * 140}ms` }}
          >
            <div
              className={styles.cardAccent}
              style={{ background: card.color }}
            />
            <div className={styles.cardHeader}>
              <span
                className={styles.cardIcon}
                style={{ color: card.color, background: `${card.color}18` }}
              >
                {card.icon}
              </span>
              <span
                className={styles.cardLabel}
                style={{ color: card.color }}
              >
                {card.label}
              </span>
            </div>
            <h3 className={styles.cardHeading}>{card.heading}</h3>

            {card.points && (
              <ul className={styles.cardList}>
                {card.points.map((p, j) => (
                  <li key={j} className={styles.cardListItem}>
                    <span
                      className={styles.cardDot}
                      style={{ background: card.color }}
                    />
                    {p}
                  </li>
                ))}
              </ul>
            )}

            {card.verse && (
              <div className={styles.cardVerse}>
                <p className={styles.cardVerseText}>{card.verse.text}</p>
                <span className={styles.cardVerseRef}>{card.verse.ref}</span>
                <p className={styles.cardVerseBody}>{card.verse.body}</p>
              </div>
            )}

            {card.outcomes && (
              <div className={styles.cardOutcomes}>
                {card.outcomes.map((o, k) => (
                  <span key={k} className={styles.cardOutcomeTag}>
                    {o}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
